/**
 * useKioskMode — Device kiosk configuration hook.
 *
 * Persists the device role in localStorage so the app auto-navigates
 * to the correct screen on startup (bypassing the dashboard).
 *
 * Supported modes:
 * - 'disabled'          → Normal mode, go to dashboard
 * - 'caisse'            → Caisse complète (/caisse)
 * - 'caisse-rapide'     → Caisse rapide plein écran (/caisse-rapide)
 * - 'caisse-restaurant' → Caisse restaurant (/caisse-restaurant)
 * - 'cuisine'           → Écran KDS cuisine (/cuisine)
 * - 'salle-display'     → Affichage salle (/salle-display)
 * - 'tablette-serveur'  → Tablette serveur WaiterPad (/chr/serveur)
 * - 'kiosque'           → Borne kiosque client (/kiosque)
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { devLog } from '@/lib/logger';

const STORAGE_KEY = 'vp_kiosk_mode';
const SESSION_BYPASS_KEY = 'vp_kiosk_session_redirected';

export type KioskMode =
  | 'disabled'
  | 'caisse'
  | 'caisse-rapide'
  | 'caisse-restaurant'
  | 'cuisine'
  | 'salle-display'
  | 'tablette-serveur'
  | 'kiosque';

export const KIOSK_MODES: { value: KioskMode; label: string; route: string }[] = [
  { value: 'disabled', label: 'Désactivé (Dashboard)', route: '/dashboard' },
  { value: 'caisse', label: 'Caisse complète', route: '/caisse' },
  { value: 'caisse-rapide', label: 'Caisse rapide', route: '/caisse-rapide' },
  { value: 'caisse-restaurant', label: 'Caisse restaurant', route: '/caisse-restaurant' },
  { value: 'cuisine', label: 'Écran cuisine (KDS)', route: '/cuisine' },
  { value: 'salle-display', label: 'Affichage salle', route: '/salle-display' },
  { value: 'tablette-serveur', label: 'Tablette serveur', route: '/chr/serveur' },
  { value: 'kiosque', label: 'Borne kiosque', route: '/kiosque' },
];

function getStoredMode(): KioskMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && KIOSK_MODES.some((m) => m.value === v)) return v as KioskMode;
  } catch {}
  return 'disabled';
}

export function useKioskMode() {
  const [mode, setModeState] = useState<KioskMode>(getStoredMode);

  const setMode = useCallback((m: KioskMode) => {
    setModeState(m);
    try {
      localStorage.setItem(STORAGE_KEY, m);
    } catch {}
    // Changer de mode annule un éventuel bypass de session (on veut que le
    // nouveau mode prenne effet immédiatement).
    try {
      sessionStorage.removeItem(SESSION_BYPASS_KEY);
    } catch {}
  }, []);

  const targetRoute = KIOSK_MODES.find((m) => m.value === mode)?.route || '/dashboard';
  const isKiosk = mode !== 'disabled';

  return { mode, setMode, isKiosk, targetRoute };
}

/**
 * Force a one-shot bypass of the kiosk auto-redirect for the current session.
 * Utilisé par les boutons "Retour au menu" dans les écrans kiosques pour
 * permettre à l'utilisateur de revenir au dashboard sans être re-redirigé.
 * Nettoyé au redémarrage de l'app (sessionStorage).
 */
export function bypassKioskThisSession() {
  try {
    sessionStorage.setItem(SESSION_BYPASS_KEY, '1');
  } catch {}
}

function hasSessionBypass(): boolean {
  try {
    return sessionStorage.getItem(SESSION_BYPASS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Auto-redirect hook — call this from Dashboard or after auth.
 * If kiosk mode is set and user is authenticated, redirect immediately.
 */
export function useKioskAutoRedirect() {
  const { user, loading } = useAuth();
  const { isKiosk, targetRoute } = useKioskMode();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading || !user) return;
    if (!isKiosk) return;

    // Bypass session : l'utilisateur a explicitement choisi de revenir au menu
    // classique (bouton "Retour au menu"). On ne re-redirige plus pour cette
    // session — le kiosque se rétablira au prochain démarrage de l'app.
    if (hasSessionBypass()) return;

    // Ne rediriger que depuis /dashboard ou /auth (pas si on est déjà sur la cible)
    const path = location.pathname;
    if (path === '/dashboard' || path === '/auth' || path === '/') {
      devLog(`[Kiosk] Auto-redirect → ${targetRoute}`);
      navigate(targetRoute, { replace: true });
    }
  }, [loading, user, isKiosk, targetRoute, navigate, location.pathname]);
}
